<template>
  <div class="space-y-3">
    <div class="flex items-center justify-between px-6">
      <lf-heading3 v-tooltip="activeAttribute.name" data-cy="attribute-name">
        {{ activeAttribute.name }}
      </lf-heading3>
      <div class="flex items-center space-x-4">
        <a
          :href="attributeLink.href"
          target="_blank"
          rel="noopener noreferrer"
          data-cy="button-open-new-tab"
        >
          <icon-base class="hover:text-gray-500" :icon="IconVisit" />
        </a>
        <icon-base
          class="cursor-pointer hover:text-gray-500"
          :icon="IconClear"
          data-cy="button-close"
          @click="$emit('update:active', -1)"
        />
      </div>
    </div>
    <hr />
    <div class="overflow-hidden">
      <div class="max-h-50-screen overflow-auto px-3">
        <div v-if="loading" class="min-h-10 min-w-10">
          <loader is-loading />
        </div>
        <custom-attribute-preview-formula
          v-else-if="activeAttribute.is_legacy"
          class="block rounded-sm border border-blue-200 p-3"
          data-cy="custom-attribute-legacy-preview-formula"
          :formula="activeAttribute.content?.formula || []"
        />
        <formula-editor
          v-else
          disabled
          :model-value="activeAttribute.tokens || []"
        />
      </div>
    </div>
    <hr />
    <div class="flex justify-between px-6">
      <span data-cy="count-indicator">
        {{ activeIndex + 1 }}/{{ attributes.length }}
      </span>
      <div class="space-x-4">
        <icon-base
          data-cy="button-previous"
          class="-scale-x-100"
          :class="
            isFirstActive
              ? 'cursor-not-allowed'
              : 'cursor-pointer hover:text-gray-500'
          "
          :icon="IconChevronRight"
          view-box="0 0 10 10"
          @click="!isFirstActive && $emit('update:active', activeIndex - 1)"
        />
        <icon-base
          data-cy="button-next"
          :class="
            isLastActive
              ? 'cursor-not-allowed'
              : 'cursor-pointer hover:text-gray-500'
          "
          :icon="IconChevronRight"
          view-box="0 0 10 10"
          @click="!isLastActive && $emit('update:active', activeIndex + 1)"
        />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";

import { ROUTE_CUSTOM_ATTRIBUTES_BUILDER_VIEW } from "@/router/routes";

import FormulaEditor from "@/components/customAttributesV2/FormulaEditor.vue";
import IconChevronRight from "@/components/icons/IconChevronRight.vue";
import IconClear from "@/components/icons/IconClear.vue";
import IconVisit from "@/components/icons/IconVisit.vue";
import LfHeading3 from "@/components/ui/text/LfHeading3.vue";
import CustomAttributePreviewFormula from "@/views/profile/components/dataOrchestration/configPanel/customAttributes/CustomAttributePreviewFormula.vue";

import type { CustomAttribute } from "@/models/customAttributes/customAttribute";

defineEmits<{
  "update:active": [index: number];
}>();

const props = defineProps<{
  attributes: CustomAttribute[];
  activeIndex: number;
  loading: boolean;
}>();

const router = useRouter();

const activeAttribute = computed(() => props.attributes[props.activeIndex]);

const isFirstActive = computed(() => props.activeIndex === 0);
const isLastActive = computed(
  () => props.activeIndex === props.attributes.length - 1
);

const attributeLink = computed(() => {
  return router.resolve({
    name: ROUTE_CUSTOM_ATTRIBUTES_BUILDER_VIEW,
    params: { id: activeAttribute.value.id },
  });
});
</script>
